import { Link } from "react-router-dom";
import { LayoutDashboard, FileText, Users, Mail, CalendarCheck, ShieldCheck, ArrowUpRight, PlayCircle, KeyRound, Lock } from "lucide-react";
import { SiteHeader, SiteFooter, PageHero } from "./SiteLayout";
import { usePageMeta } from "./siteContent";

const FEATURES = [
  { icon: LayoutDashboard, title: "Customer portal logins", text: "Give each of your customers their own login — they see their quotes, invoices, documents and event reports in one place." },
  { icon: FileText, title: "Quotes & invoices", text: "Build and send professional quotes and invoices in seconds, track who's paid, and chase the ones who haven't." },
  { icon: Users, title: "Full CRM", text: "Every customer, contact, call and job in one place — with a to-do list that keeps follow-ups from slipping." },
  { icon: Mail, title: "Email system", text: "Your business email built in — send, receive and reply without leaving the system." },
  { icon: CalendarCheck, title: "Calendar & staffing", text: "Book events, offer shifts to your team and see who's working where at a glance." },
  { icon: ShieldCheck, title: "CQC-compliant patient reporting", text: "Full patient care records, observations and end-of-event reports built to CQC-equivalent standards." },
];

export default function SiteSoftware() {
  usePageMeta("Event Medical Business Software | Portal, CRM & Invoicing | WEMS",
    "Run your event medical service on the WEMS software — customer portal logins, quotes & invoices, full CRM, email system and CQC-compliant patient reporting. One-off build fee, then just £25 a month.", "", "software");
  return (
    <div className="site-font bg-[#0B0F14]" data-testid="site-software">
      <SiteHeader />
      <PageHero title="The software behind WEMS" subtitle="The same system that runs our events, bookings, invoicing and patient care — and it can run your service too." image="/site-assets/hero5.jpg" />

      <section className="py-16 border-b border-white/5" data-testid="software-demo">
        <div className="container-x max-w-3xl text-center">
          <span className="eyebrow">See it in action</span>
          <h2 className="section-title mt-4">Want a demonstration?</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            We'll walk you through the whole system live — bookings, invoices, the CRM, email and patient reporting.
            Every demonstration uses <strong className="text-white">dummy data only</strong>: no real customer or patient
            information is ever shown, so nothing ever puts anyone's data at risk.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact-us" className="btn btn-primary" data-testid="software-demo-btn"><PlayCircle size={16} /> Book a demonstration</Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="software-features">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="eyebrow">What it does</span>
            <h2 className="section-title mt-4">One system for the whole job</h2>
            <p className="mt-4 text-slate-300">This is what your customers and your team can do with it.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="card-soft p-6 card-lift" data-testid={`software-feature-${i}`}>
                <span className="inline-flex w-11 h-11 rounded-xl bg-[#00D2C2]/15 text-[#00D2C2] items-center justify-center"><f.icon size={20} /></span>
                <h3 className="mt-4 font-bold text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-y border-white/5 bg-[#121820]" data-testid="software-login">
        <div className="container-x max-w-3xl text-center">
          <span className="inline-flex w-14 h-14 rounded-full bg-[#00D2C2]/15 text-[#00D2C2] items-center justify-center"><KeyRound size={26} /></span>
          <h2 className="mt-5 font-serif-site text-white text-3xl md:text-4xl font-extrabold tracking-tight">Already a WEMS customer?</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            <strong className="text-white">Just ask for your login.</strong> If you've signed up with us, you can have one —
            your own portal with your quotes, invoices, documents and event reports, whenever you need them.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact-us" className="btn btn-primary" data-testid="software-ask-login-btn">Ask for your login</Link>
            <Link to="/portal/login" className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-600 text-white font-bold text-sm hover:border-[#00D2C2] hover:text-[#00D2C2] transition-colors" data-testid="software-portal-login-btn">Customer login <ArrowUpRight size={15} /></Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="software-pricing">
        <div className="container-x max-w-3xl">
          <div className="rounded-3xl bg-gradient-to-br from-[#0F2B32] via-[#16202C] to-[#0B0F14] border border-[#00D2C2]/25 px-8 md:px-14 py-14 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#10B981]/20 blur-2xl" />
            <span className="eyebrow relative">Run your own service on it</span>
            <h2 className="relative mt-4 font-serif-site text-white text-3xl md:text-4xl font-extrabold tracking-tight">One-off build fee, then just £25 a month</h2>
            <p className="relative mt-4 text-slate-300 max-w-xl mx-auto">
              That's it — your own copy of the whole system at a genuinely low monthly cost, running your:
            </p>
            <ul className="relative mt-6 grid sm:grid-cols-2 gap-2 max-w-xl mx-auto text-left">
              {["Email system", "Invoice & quote system", "Full CRM system", "CQC-compliant patient reports"].map((x, i) => (
                <li key={x} className="flex items-center gap-2.5 text-sm text-slate-200" data-testid={`software-include-${i}`}>
                  <ShieldCheck size={15} className="text-[#10B981] shrink-0" /> {x}
                </li>
              ))}
            </ul>
            <div className="relative mt-8">
              <Link to="/contact-us" className="btn btn-primary" data-testid="software-pricing-btn">Talk to us about it</Link>
            </div>
            <p className="relative mt-4 text-xs text-slate-400 flex items-center justify-center gap-1.5"><Lock size={12} /> Your data stays yours — hosted securely, never shared.</p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
